import { useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '@/lib/supabase';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { useModalOverflow } from '@/hooks/useModalOverflow';

interface AnularProgressaoModalProps {
  progressao: {
    id: string;
    funcionarioId: string;
    funcionarioNome: string;
    dataProgressao: string;
    nivelAnterior: number;
    nivelNovo: number;
    pontosDescontados: number;
  };
  onClose: () => void;
  onSuccess: () => void;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${day}/${month}/${year}`;
}

export default function AnularProgressaoModal({ progressao, onClose, onSuccess }: AnularProgressaoModalProps) {
  const { funcionarios, updateFuncionario } = useFuncionarios();
  const { animating, handleClose } = useModalAnimation(onClose);
  useModalOverflow(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const initials = progressao.funcionarioNome
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  async function handleAnular() {
    setSaving(true);
    setError('');
    try {
      const func = funcionarios.find((f) => f.id === progressao.funcionarioId);
      if (!func) throw new Error('Funcionário não encontrado.');

      const { error: delErr } = await supabase
        .from('progressoes')
        .delete()
        .eq('id', progressao.id);
      if (delErr) throw new Error(delErr.message);

      const novosPontosDescontados = Math.max(0, (func.pontosDescontados ?? 0) - progressao.pontosDescontados);

      const { data: restantes } = await supabase
        .from('progressoes')
        .select('data_progressao, nivel_novo')
        .eq('funcionario_id', progressao.funcionarioId)
        .order('data_progressao', { ascending: false })
        .limit(1);

      const ultimaProgressaoRestante = restantes && restantes.length > 0
        ? (restantes[0].data_progressao as string)
        : '';

      await updateFuncionario({
        ...func,
        nivelAtual: progressao.nivelAnterior,
        novoNivel2025: progressao.nivelAnterior,
        pontosDescontados: novosPontosDescontados,
        ultimaProgressao: ultimaProgressaoRestante,
      }, func.id);

      onSuccess();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao anular progressão.');
    } finally {
      setSaving(false);
    }
  }

  const modal = (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-5xl max-h-[96vh] overflow-hidden border border-zinc-100 flex flex-col ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between flex-shrink-0 bg-zinc-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-rose-100 flex-shrink-0">
              <i className="ri-arrow-go-back-line text-rose-600 text-lg" />
            </div>
            <div>
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-0.5">Anular</p>
              <h2 className="text-[15px] font-bold text-zinc-900 leading-tight">Progressão</h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Funcionário */}
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3 bg-white flex-shrink-0">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-zinc-900 text-white text-[12px] font-bold flex-shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-zinc-800 truncate">{progressao.funcionarioNome}</p>
            <p className="text-[11px] text-zinc-400">Progressão de {formatDate(progressao.dataProgressao)}</p>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">

          {/* Aviso */}
          <div className="flex items-start gap-3 bg-rose-50 border border-rose-200 rounded-xl p-4">
            <div className="w-8 h-8 flex items-center justify-center bg-rose-100 rounded-lg text-rose-600 flex-shrink-0">
              <i className="ri-error-warning-line text-sm" />
            </div>
            <div>
              <p className="text-[12px] font-bold text-rose-800 mb-1">Esta ação irá reverter a progressão</p>
              <p className="text-[11px] text-rose-700 leading-relaxed">
                O registo será eliminado, o nível revertido para <strong>{progressao.nivelAnterior}</strong> e os pontos devolvidos ao ciclo atual.
              </p>
            </div>
          </div>

          {/* Detalhes da progressão */}
          <div>
            <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">Detalhes da Progressão</p>
            <div className="bg-zinc-50 rounded-xl border border-zinc-100 overflow-hidden">
              <div className="grid grid-cols-2 divide-x divide-zinc-100">
                <div className="p-4 text-center">
                  <p className="text-[11px] text-zinc-400 mb-1">Data</p>
                  <p className="text-[14px] font-bold text-zinc-700">{formatDate(progressao.dataProgressao)}</p>
                </div>
                <div className="p-4 text-center">
                  <p className="text-[11px] text-zinc-400 mb-1">Pontos a devolver</p>
                  <p className="text-[14px] font-bold text-emerald-600">+{progressao.pontosDescontados} pts</p>
                </div>
              </div>
              <div className="border-t border-zinc-100 p-4">
                <div className="flex items-center justify-center gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white border border-zinc-200 mx-auto mb-1.5">
                      <span className="text-[18px] font-bold text-zinc-700">{progressao.nivelNovo}</span>
                    </div>
                    <p className="text-[11px] text-zinc-500">Nível atual</p>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-rose-100 text-rose-600">
                      <i className="ri-arrow-left-line text-xs" />
                    </div>
                    <span className="text-[10px] text-rose-600 font-semibold">reverter</span>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-rose-50 border border-rose-200 mx-auto mb-1.5">
                      <span className="text-[18px] font-bold text-rose-600">{progressao.nivelAnterior}</span>
                    </div>
                    <p className="text-[11px] text-rose-600 font-medium">Após anulação</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Confirmação */}
          <div
            onClick={() => setConfirmed((v) => !v)}
            className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all ${
              confirmed ? 'bg-rose-50 border-rose-300' : 'bg-zinc-50 border-zinc-200 hover:border-zinc-300'
            }`}
          >
            <div className={`w-5 h-5 flex items-center justify-center rounded border-2 flex-shrink-0 transition-all ${
              confirmed ? 'bg-rose-500 border-rose-500' : 'border-zinc-300 bg-white'
            }`}>
              {confirmed && <i className="ri-check-line text-white text-xs" />}
            </div>
            <p className={`text-[12px] font-medium ${confirmed ? 'text-rose-700' : 'text-zinc-600'}`}>
              Confirmo que pretendo anular esta progressão
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-[12px] text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2.5">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-sm" />
              </div>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-2.5 flex-shrink-0 bg-zinc-50/40">
          <button
            onClick={handleClose}
            className="whitespace-nowrap px-4 py-2 text-[13px] font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            onClick={handleAnular}
            disabled={saving || !confirmed}
            className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-[13px] font-medium bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving
              ? <><i className="ri-loader-4-line animate-spin text-sm" />A anular...</>
              : <><i className="ri-arrow-go-back-line text-sm" />Confirmar Anulação</>
            }
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}